import { datadogRum } from '@datadog/browser-rum';
import createError, { type HttpError } from 'http-errors';

// API Services
export enum ApiService {
  AUTHORIZATION = 'authorization',
  COLLATERAL = 'collateral',
  FUNDING = 'funding',
  MEASUREMENT = 'measurement',
  NOTIFICATION = 'notification',
  DEALS = 'deals',
}

// Error Categories based on RFC 7807 and common standards
export enum ErrorCategory {
  // Authentication & Authorization (OAuth 2.0)
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  INVALID_TOKEN = 'invalid_token',
  INSUFFICIENT_SCOPE = 'insufficient_scope',

  // Client Errors (RFC 7807)
  VALIDATION = 'validation',
  INVALID_REQUEST = 'invalid_request',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  RATE_LIMITED = 'rate_limited',
  PAYLOAD_TOO_LARGE = 'payload_too_large',

  // Server Errors (RFC 7807)
  INTERNAL = 'internal',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  EXTERNAL_SERVICE = 'external_service',
  DATABASE = 'database',
  CACHE = 'cache',

  // Business Logic
  BUSINESS_RULE = 'business_rule',
  WORKFLOW = 'workflow',
  STATE_CONFLICT = 'state_conflict',
}

// Standard response error format
export interface ResponseErrorData {
  message: string;
  statusCode: number;
  cause?: unknown;
  category?: ErrorCategory;
  service?: ApiService;
  metadata?: Record<string, unknown>;
}

// ResponseError class for throwing errors with status codes
export class ResponseError extends Error {
  statusCode: number;
  cause?: unknown;
  category?: ErrorCategory;
  service?: ApiService;
  metadata?: Record<string, unknown>;

  constructor(message: string, statusCode: number, data?: Partial<ResponseErrorData>) {
    super(message);
    this.name = 'ResponseError';
    this.statusCode = statusCode;
    this.cause = data?.cause;
    this.category = data?.category;
    this.service = data?.service;
    this.metadata = data?.metadata;
  }
}

// Enhanced error with additional context
export interface EnhancedError extends HttpError {
  category?: ErrorCategory;
  service?: ApiService;
  metadata?: Record<string, unknown>;
}

// Helper to create and track errors
export const createAppError = (
  status: number,
  message: string,
  options?: {
    category?: ErrorCategory;
    service?: ApiService;
    cause?: unknown;
    metadata?: Record<string, unknown>;
  },
): EnhancedError => {
  const error = createError(status, message, { cause: options?.cause }) as EnhancedError;

  // Add custom properties
  error.category = options?.category;
  error.service = options?.service;
  error.metadata = options?.metadata;

  // Track in Datadog
  try {
    datadogRum.addError(error, {
      statusCode: status,
      category: options?.category,
      service: options?.service,
      ...options?.metadata,
    });
  } catch (e) {
    console.error('Failed to track error in Datadog:', e);
  }

  return error;
};

// Authentication & Authorization Errors
export const createAuthError = (message = 'Authentication required', cause?: unknown) =>
  createAppError(401, message, {
    category: ErrorCategory.AUTHENTICATION,
    cause,
  });

export const createForbiddenError = (message = 'Permission denied', cause?: unknown) =>
  createAppError(403, message, {
    category: ErrorCategory.AUTHORIZATION,
    cause,
  });

export const createInvalidTokenError = (message = 'Invalid or expired token', cause?: unknown) =>
  createAppError(401, message, {
    category: ErrorCategory.INVALID_TOKEN,
    cause,
  });

export const createInsufficientScopeError = (message = 'Insufficient permissions', cause?: unknown) =>
  createAppError(403, message, {
    category: ErrorCategory.INSUFFICIENT_SCOPE,
    cause,
  });

// Client Errors
export const createValidationError = (message = 'Invalid input', cause?: unknown, metadata?: Record<string, unknown>) =>
  createAppError(400, message, {
    category: ErrorCategory.VALIDATION,
    cause,
    metadata,
  });

export const createInvalidRequestError = (message = 'Invalid request', cause?: unknown) =>
  createAppError(400, message, {
    category: ErrorCategory.INVALID_REQUEST,
    cause,
  });

export const createNotFoundError = (message = 'Resource not found', cause?: unknown) =>
  createAppError(404, message, {
    category: ErrorCategory.NOT_FOUND,
    cause,
  });

export const createConflictError = (message = 'Resource conflict', cause?: unknown) =>
  createAppError(409, message, {
    category: ErrorCategory.CONFLICT,
    cause,
  });

export const createRateLimitError = (message = 'Rate limit exceeded', cause?: unknown) =>
  createAppError(429, message, {
    category: ErrorCategory.RATE_LIMITED,
    cause,
  });

// Server Errors
export const createServiceError = (
  service: ApiService,
  message = 'Service error',
  cause?: unknown,
  metadata?: Record<string, unknown>,
) =>
  createAppError(500, message, {
    category: ErrorCategory.INTERNAL,
    service,
    cause,
    metadata,
  });

export const createExternalServiceError = (
  service: ApiService,
  message = 'External service error',
  cause?: unknown,
  metadata?: Record<string, unknown>,
) =>
  createAppError(503, message, {
    category: ErrorCategory.EXTERNAL_SERVICE,
    service,
    cause,
    metadata,
  });

export const createDatabaseError = (message = 'Database error', cause?: unknown) =>
  createAppError(500, message, {
    category: ErrorCategory.DATABASE,
    cause,
  });

// Business Logic Errors
export const createBusinessRuleError = (message = 'Business rule violation', cause?: unknown) =>
  createAppError(400, message, {
    category: ErrorCategory.BUSINESS_RULE,
    cause,
  });

export const createWorkflowError = (message = 'Workflow error', cause?: unknown) =>
  createAppError(400, message, {
    category: ErrorCategory.WORKFLOW,
    cause,
  });

export const createStateConflictError = (message = 'State conflict', cause?: unknown) =>
  createAppError(409, message, {
    category: ErrorCategory.STATE_CONFLICT,
    cause,
  });

// Legacy error types for backward compatibility
export class AuthenticationError extends Error {
  constructor(message = 'Authentication required') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class PermissionError extends Error {
  constructor(message = 'Permission denied') {
    super(message);
    this.name = 'PermissionError';
  }
}
